import type { NoticeType } from '@/features/teacher/types/notices';

export function NoticeRowThumbnail({ title, type }: { title: string; type: string }) {
  const normTitle = (title || '').toLowerCase();
  const normType = type.toUpperCase();

  // 1. Holiday / Vacation (Beach Thumbnail)
  if (normTitle.includes('vacation') || normTitle.includes('holiday')) {
    return (
      <div className="relative h-8 w-8 rounded-lg overflow-hidden bg-gradient-to-b from-sky-400 via-sky-300 to-amber-200 shrink-0 shadow-2xs flex items-center justify-center select-none">
        <svg viewBox="0 0 48 48" className="h-full w-full">
          <circle cx="36" cy="12" r="6" fill="#f59e0b" />
          <path d="M0 28 Q12 26 24 28 T48 28 L48 48 L0 48 Z" fill="#0284c7" opacity="0.6" />
          <path d="M0 34 Q16 32 32 35 T48 34 L48 48 L0 48 Z" fill="#fbbf24" />
          <path d="M12 22 Q19 14 26 22 Z" fill="#ec4899" />
          <line x1="19" y1="20" x2="19" y2="38" stroke="#cbd5e1" strokeWidth="1.5" />
          <path d="M4 14 Q10 10 14 16 Q8 18 4 14 Z" fill="#15803d" />
          <path d="M2 18 Q8 14 12 20 Q6 22 2 18 Z" fill="#16a34a" />
        </svg>
      </div>
    );
  }

  // 2. Parent Teacher Meeting (Two People / PTM)
  if (normTitle.includes('meeting') || normTitle.includes('ptm')) {
    return (
      <div className="relative h-8 w-8 rounded-lg overflow-hidden bg-gradient-to-tr from-purple-200 via-indigo-100 to-purple-100 shrink-0 shadow-2xs flex items-center justify-center select-none">
        <svg viewBox="0 0 48 48" className="h-6 w-6">
          <circle cx="18" cy="16" r="6" fill="#6366f1" />
          <path d="M10 36 C10 26 26 26 26 36 Z" fill="#4f46e5" />
          <circle cx="30" cy="18" r="5" fill="#f59e0b" />
          <path d="M23 36 C23 28 37 28 37 36 Z" fill="#d97706" />
        </svg>
      </div>
    );
  }

  // 3. Exam / Test (Exam paper)
  if (normTitle.includes('exam') || normTitle.includes('test') || normType === 'ACADEMIC') {
    return (
      <div className="relative h-8 w-8 rounded-lg overflow-hidden bg-gradient-to-br from-sky-100 via-indigo-50 to-blue-200 shrink-0 shadow-2xs flex items-center justify-center select-none">
        <svg viewBox="0 0 48 48" className="h-6 w-6">
          <rect x="12" y="8" width="24" height="32" rx="3" fill="#ffffff" stroke="#93c5fd" strokeWidth="1.5" />
          {/* A paper, not the word "EXAM": this icon also stands for any academic
              notice, and a label on it would claim every one was an exam. */}
          <line x1="16" y1="18" x2="32" y2="18" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round" />
          <line x1="16" y1="24" x2="32" y2="24" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="16" y1="29" x2="28" y2="29" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="16" y1="34" x2="24" y2="34" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
    );
  }

  // 4. Drawing / Competition / Event (Golden Trophy)
  if (normTitle.includes('competition') || normTitle.includes('drawing') || normTitle.includes('art') || normType === 'EVENT') {
    return (
      <div className="relative h-8 w-8 rounded-lg overflow-hidden bg-gradient-to-tr from-amber-400 to-yellow-300 shrink-0 shadow-2xs flex items-center justify-center select-none">
        <svg viewBox="0 0 48 48" className="h-5 w-5">
          <path d="M14 12 L34 12 C34 22 28 26 24 26 C20 26 14 22 14 12 Z" fill="#ffffff" opacity="0.95" />
          <path d="M14 14 C10 14 8 18 10 22 C12 25 15 24 15 24" stroke="#ffffff" strokeWidth="2" fill="none" strokeLinecap="round" />
          <path d="M34 14 C38 14 40 18 38 22 C36 25 33 24 33 24" stroke="#ffffff" strokeWidth="2" fill="none" strokeLinecap="round" />
          <rect x="22" y="26" width="4" height="8" fill="#ffffff" opacity="0.9" />
          <rect x="16" y="34" width="16" height="4" rx="1.5" fill="#ffffff" />
        </svg>
      </div>
    );
  }

  // 5. Library / Books
  if (normTitle.includes('library') || normTitle.includes('book')) {
    return (
      <div className="relative h-8 w-8 rounded-lg overflow-hidden bg-gradient-to-tr from-emerald-400 to-teal-300 shrink-0 shadow-2xs flex items-center justify-center select-none">
        <svg viewBox="0 0 48 48" className="h-5 w-5">
          <rect x="10" y="28" width="28" height="6" rx="1" fill="#ffffff" opacity="0.9" />
          <rect x="12" y="20" width="24" height="6" rx="1" fill="#ec4899" />
          <rect x="14" y="12" width="20" height="6" rx="1" fill="#38bdf8" />
        </svg>
      </div>
    );
  }

  // 6. Health / Medical
  if (normTitle.includes('health') || normTitle.includes('camp') || normTitle.includes('checkup')) {
    return (
      <div className="relative h-8 w-8 rounded-lg overflow-hidden bg-gradient-to-tr from-rose-400 to-pink-300 shrink-0 shadow-2xs flex items-center justify-center select-none">
        <svg viewBox="0 0 48 48" className="h-5 w-5">
          <rect x="12" y="16" width="24" height="18" rx="3" fill="#ffffff" />
          <path d="M21 16 L21 13 C21 11.5 27 11.5 27 13 L27 16" stroke="#ffffff" strokeWidth="2" fill="none" />
          <rect x="22" y="20" width="4" height="10" rx="1" fill="#e11d48" />
          <rect x="19" y="23" width="10" height="4" rx="1" fill="#e11d48" />
        </svg>
      </div>
    );
  }

  // 7. Transport / School Bus
  if (normTitle.includes('transport') || normTitle.includes('bus') || normTitle.includes('route')) {
    return (
      <div className="relative h-8 w-8 rounded-lg overflow-hidden bg-gradient-to-tr from-sky-400 to-cyan-300 shrink-0 shadow-2xs flex items-center justify-center select-none">
        <svg viewBox="0 0 48 48" className="h-5 w-5">
          <rect x="8" y="14" width="32" height="20" rx="4" fill="#fbbf24" stroke="#d97706" strokeWidth="1.5" />
          <rect x="12" y="18" width="6" height="6" rx="1" fill="#0284c7" />
          <rect x="21" y="18" width="6" height="6" rx="1" fill="#0284c7" />
          <rect x="30" y="18" width="6" height="6" rx="1" fill="#0284c7" />
          <circle cx="14" cy="34" r="3.5" fill="#1e293b" />
          <circle cx="34" cy="34" r="3.5" fill="#1e293b" />
        </svg>
      </div>
    );
  }

  // 8. General / Announcement / Circular
  return (
    <div className="relative h-8 w-8 rounded-lg overflow-hidden bg-gradient-to-tr from-indigo-500 to-purple-500 shrink-0 shadow-2xs flex items-center justify-center select-none">
      <svg viewBox="0 0 48 48" className="h-4.5 w-4.5" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 16 L20 16 L32 8 L32 36 L20 28 L8 28 Z" fill="#ffffff" opacity="0.9" />
        <path d="M38 18 C40 20 40 24 38 26" stroke="#ffffff" strokeWidth="3" />
      </svg>
    </div>
  );
}

/**
 * The artwork over an open notice.
 *
 * Picked from the type the author chose, and the only words on it are the
 * notice's own title. It used to guess a topic from keywords in the title and
 * print a caption of its own — "Summer Vacation", "Parent–Teacher Meeting",
 * "Examinations & Assessment" — over notices that said no such thing.
 */
export function NoticeBannerIllustration({ title, type }: { title: string; type: NoticeType }) {
  const caption = (
    <span className="text-xl sm:text-2xl font-black tracking-tight text-white drop-shadow-sm text-center px-4 line-clamp-3 break-words">
      {title}
    </span>
  );

  if (type === 'EVENT') {
    return (
      <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden bg-gradient-to-br from-indigo-700 via-purple-700 to-indigo-900 shadow-xs flex flex-col items-center justify-center p-6 text-white select-none">
        <div className="h-14 w-14 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center mb-2">
          <svg viewBox="0 0 24 24" className="h-8 w-8 text-white fill-none stroke-current" strokeWidth="2" aria-hidden="true">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>
        {caption}
      </div>
    );
  }

  if (type === 'ACADEMIC') {
    return (
      <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden bg-gradient-to-br from-blue-700 via-indigo-700 to-sky-600 shadow-xs flex flex-col items-center justify-center p-6 text-white select-none">
        <div className="h-14 w-14 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center mb-2">
          <svg viewBox="0 0 24 24" className="h-8 w-8 text-white fill-none stroke-current" strokeWidth="2" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
        </div>
        {caption}
      </div>
    );
  }

  return (
    <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden bg-gradient-to-br from-indigo-700 via-indigo-600 to-sky-600 shadow-xs flex flex-col items-center justify-center p-6 text-white select-none">
      <div className="h-14 w-14 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center mb-2">
        <svg viewBox="0 0 24 24" className="h-8 w-8 text-white fill-none stroke-current" strokeWidth="2" aria-hidden="true">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>
      </div>
      {caption}
    </div>
  );
}
